import { useEffect, useMemo, useSyncExternalStore } from 'react';

import {
  selectActiveBranchCountryId,
  selectActiveBranchName,
  selectUser,
  useAppSelector,
} from '@app/store';
import {
  getActiveBranchCountryIdFromSession,
  getActiveBranchIdFromSession,
  getActiveBranchNameFromSession,
  getActiveBranchUserIdFromSession,
  setActiveBranchSession,
  subscribeActiveBranchSession,
} from '@app/utils';

export const useUserPrint = () => {
  const user = useAppSelector(selectUser);
  const reduxActiveBranchName = useAppSelector(selectActiveBranchName);
  const reduxActiveBranchCountryId = useAppSelector(
    selectActiveBranchCountryId
  );

  const sessionActiveBranchId = useSyncExternalStore(
    subscribeActiveBranchSession,
    getActiveBranchIdFromSession,
    () => null
  );
  const sessionActiveBranchName = useSyncExternalStore(
    subscribeActiveBranchSession,
    getActiveBranchNameFromSession,
    () => null
  );
  const sessionActiveBranchCountryId = useSyncExternalStore(
    subscribeActiveBranchSession,
    getActiveBranchCountryIdFromSession,
    () => null
  );
  const sessionActiveBranchUserId = useSyncExternalStore(
    subscribeActiveBranchSession,
    getActiveBranchUserIdFromSession,
    () => null
  );

  // Сессия вкладки принадлежит конкретному пользователю: после перелогина под
  // другим профилем в той же вкладке (протухший токен, вход без «Выйти»)
  // чужие значения игнорируются — до пересева эффектом ниже действует филиал
  // из данных вошедшего пользователя. Сессия без владельца — вкладка, открытая
  // до появления привязки; её значения считаются своими и получают владельца.
  const isSessionForeign =
    sessionActiveBranchUserId !== null &&
    user?.userId != null &&
    sessionActiveBranchUserId !== user.userId;

  const activeBranchId =
    (isSessionForeign ? null : sessionActiveBranchId) ??
    user?.activeBranchId ??
    null;
  const activeBranchName =
    (isSessionForeign ? null : sessionActiveBranchName) ??
    reduxActiveBranchName;
  const activeBranchCountryId =
    (isSessionForeign ? null : sessionActiveBranchCountryId) ??
    reduxActiveBranchCountryId;

  useEffect(() => {
    if (user?.userId == null) return;

    const sessionOwnerUserId = getActiveBranchUserIdFromSession();
    const sessionBranchId = getActiveBranchIdFromSession();

    if (sessionBranchId !== null && sessionOwnerUserId === user.userId) return;

    if (sessionBranchId !== null && sessionOwnerUserId === null) {
      setActiveBranchSession({ activeBranchUserId: user.userId });
      return;
    }

    if (user.activeBranchId == null) return;

    setActiveBranchSession({
      activeBranchId: user.activeBranchId,
      activeBranchName: reduxActiveBranchName,
      activeBranchCountryId: reduxActiveBranchCountryId,
      activeBranchUserId: user.userId,
    });
  }, [
    user?.userId,
    user?.activeBranchId,
    reduxActiveBranchName,
    reduxActiveBranchCountryId,
  ]);

  const userPrint = useMemo(
    () => ({
      userId: user?.userId || null,
      database: user?.database || null,
      activeBranchId,
    }),
    [user?.userId, user?.database, activeBranchId]
  );

  const params = useMemo(
    () => ({
      UserId: userPrint.userId as number,
      Database: userPrint.database as string,
      ActiveBranchId: userPrint.activeBranchId as number,
    }),
    [userPrint]
  );

  const isAuth = useMemo(
    () =>
      Boolean(
        userPrint.userId && userPrint.database && userPrint.activeBranchId
      ),
    [userPrint]
  );

  return {
    ...userPrint,
    userPrint,
    params,
    user,
    isAuth,
    activeBranchName,
    activeBranchCountryId,
  };
};
